import type { Address, ChainId } from '@morpho-org/blue-sdk'
import type { InterfaceToken } from '@morpho-org/dapp-core'
import type { DialogOptions } from '@morpho-org/ui-v6/client'
import type { ToggleDialog } from '@morpho-org/ui-v6/contexts'
import type { ReactNode } from 'react'

import { isDefined } from '@morpho-org/morpho-ts'

import type { InKindRedemptionEntrySource } from '@/lib/analytics/client/events'

import { trackInKindRedemptionEntryClicked } from '@/lib/analytics/client/trackers/in-kind-redemption'

import VaultV2InKindRedemptionDialog from './VaultV2InKindRedemptionDialog'

/** Overrides the withdraw form's primary button at tier 3, where a plain withdraw cannot run. */
export interface InKindRedemptionAction {
  label: string
  tooltipContent: ReactNode
  onClick: () => void
}

/** Shared IKR entry CTA for the vault-page form and the portfolio position manager. */
export const buildInKindRedemptionEntryAction = ({
  source,
  chainId,
  vaultAddress,
  requestedExitAssets,
  userPosition,
  vaultAsset,
  toggleDialog,
  dialogOptions,
  onTxSuccess
}: {
  source: InKindRedemptionEntrySource
  chainId: ChainId | undefined
  vaultAddress: Address
  requestedExitAssets: bigint
  userPosition: bigint | undefined
  vaultAsset: InterfaceToken | null | undefined
  toggleDialog: ToggleDialog
  dialogOptions?: DialogOptions
  onTxSuccess?: () => void
}): InKindRedemptionAction => ({
  label: 'Choose a Market',
  tooltipContent: (
    <span className="text-3xs">
      This Vault cannot serve the full amount from its available liquidity. In-kind redemption pays
      out the liquidity it has and supplies the rest, net of the redemption fee, to a Market you
      choose, up to what that Market can take.
    </span>
  ),
  onClick: () => {
    if (!isDefined(userPosition) || !isDefined(chainId)) return
    trackInKindRedemptionEntryClicked({ source, chainId, vaultAddress })
    toggleDialog(
      <VaultV2InKindRedemptionDialog
        source={source}
        vaultAddress={vaultAddress}
        chainId={chainId}
        requestedExitAssets={requestedExitAssets}
        userPosition={userPosition}
        vaultAsset={vaultAsset}
        dialogOptions={dialogOptions}
        onTxSuccess={onTxSuccess}
      />,
      { ...dialogOptions, maxWidth: '672px', ariaLabel: 'In-kind redemption' }
    )
  }
})
